<template>
    <a
        v-if="href"
        :id="id || undefined"
        tabindex="0"
        :class="`color-${color} ${icon || iconAfter ? 'btn-icon': ''} ${mini ? 'mini' : ''}`"
        :disabled="disabled"
        :href="href"
        :target="target !== 'inertia' ? target : null"
        @click="hrefHandler"
    >
        <DIcon
            v-if="icon && !loading"
            :name="icon"
        />
        <DIcon
            v-if="loading"
            name="loading"
            class="loading"
        />
        <span v-if="label">{{ label }}</span>
        <DIcon
            v-if="iconAfter && !loading"
            :name="iconAfter"
        />
    </a>
    <button
        v-else
        v-bind="$attrs"
        :id="id || undefined"
        tabindex="0"
        type="button"
        :class="`color-${color} ${variant === 'ghost' ? 'ghost' : ''} ${icon || iconAfter ? 'btn-icon': ''} ${mini ? 'mini' : ''} ${minHeight50 ? 'min-height-50' : ''} ${dragAndDropBtn ? 'drag-and-drop-btn' : ''}`"
        :disabled="disabled"
        @click="clickHandler"
    >
        <DIcon
            v-if="icon && !loading"
            :name="icon"
        />
        <DIcon
            v-if="loading"
            name="loading"
            class="loading"
        />
        <span v-if="label">{{ label }}</span>
        <DIcon
            v-if="iconAfter && !loading"
            :name="iconAfter"
        />
    </button>
</template>

<script>
export default {
    name: 'DButton',
    props: {
        icon: {
            type: [String, Boolean],
            required: false,
            default: false
        },
        // Same contract as `icon`, but rendered after the label -- for buttons
        // whose icon reads as a direction or consequence rather than a category,
        // e.g. a "Next" pager control.
        iconAfter: {
            type: [String, Boolean],
            required: false,
            default: false
        },
        label: {
            type: String,
            default: '',
            required: false
        },
        loading: {
            type: Boolean,
            required: false,
            default: false
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false
        },
        variant: {
            type: String,
            required: false,
            default: 'solid'
        },
        mini: {
            type: Boolean,
            required: false,
            default: false
        },
        color: {
            type: String,
            required: false,
            default: 'secondary'
        },
        href: {
            type: String,
            required: false,
            default: null
        },
        target: {
            type: String,
            required: false,
            default: 'inertia' // Special Value inertia will do $inertia.get on href in hrefHandler
        },
        minHeight50: {
            type: Boolean,
            required: false,
            default: false
        },
        dragAndDropBtn: {
            type: Boolean,
            required: false,
            default: false
        },
        id: {
            type: String,
            required: false,
            default: ''
        }
    },
    emits: ['click'],
    methods: {
        hrefHandler (e) {
            if (this.disabled) {
                e.preventDefault()
                return
            }

            if (this.target === 'inertia') {
                e.preventDefault()
                this.$inertia.get(this.href)
            }
        },
        clickHandler (event) {
            if (this.disabled) {
                return
            }

            this.$emit('click', event)
        }
    }
}
</script>

<style lang="scss" scoped>
button, a {
    font-family: 'Open Sans', sans-serif;
    font-size: 14px;
    padding: 9px 15px;
    border-radius: 5px;
    display: inline-flex;
    grid-template-columns: 1fr;
    grid-gap: 7px;
    align-items: center;
    transition: 0.2s ease-in-out background, 0.2s ease-in-out color;
    border: 0;
    max-width: none;

    &:hover {
        text-decoration: none;
    }

    &:not(.btn-icon) {
        padding: 12px 15px;
    }
    &.mini {
        padding: 5px 10px;

        &:not(.btn-icon) {
            padding: 8px 15px;
        }
    }
    & .loading {
        animation: loading 0.4s linear infinite;
    }

    &.color-primary {
        background: var(--colorPrimary);
        color: var(--colorPrimaryText);
    }
    &.color-secondary {
        background: var(--colorSecondary);
        color: var(--colorSecondaryText);
    }
    &.color-danger {
        background: var(--colorDanger);
        color: var(--colorDangerText);
    }
    &.color-success {
        background: var(--colorSuccess);
        color: var(--colorSuccessText);
    }
    &:disabled {
        cursor: not-allowed;
        opacity: 0.4;
    }
    &:not(:disabled):hover {
        &.color-primary {
            background: var(--colorPrimaryHover);
        }
        &.color-secondary {
            background: var(--colorSecondaryHover);
        }
        &.color-danger {
            background: var(--colorDangerHover);
        }
        &.color-success {
            background: var(--colorSuccessHover);
        }
    }
    &:not(:disabled):active {
        &.color-primary {
            background: var(--colorPrimaryActive);
        }
        &.color-secondary {
            background: var(--colorSecondaryActive);
        }
        &.color-danger {
            background: var(--colorDangerActive);
        }
        &.color-success {
            background: var(--colorSuccessActive);
        }
    }

    &.ghost {
        background: transparent;
        border: 0;

        &.color-primary {
            color: var(--colorPrimary);
        }
        &.color-secondary {
            color: var(--colorSecondary);
        }
        &.color-danger {
            color: var(--colorDanger);
        }
        &.color-success {
            color: var(--colorSuccess);
        }

        &:not(:disabled):hover {
            &.color-primary {
                background: var(--colorPrimary);
                color: var(--colorPrimaryText);
            }
            &.color-secondary {
                background: var(--colorSecondary);
                color: var(--colorSecondaryText);
            }
            &.color-danger {
                background: var(--colorDanger);
                color: var(--colorDangerText);
            }
            &.color-success {
                background: var(--colorSuccess);
                color: var(--colorSuccessText);
            }
        }
    }
}

@keyframes loading {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.min-height-50 {
    min-height: 50px;
}
.drag-and-drop-btn {
    cursor: move;
}
</style>
